import {
  Component, OnDestroy, OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { UsersService } from 'src/app/services/users.service';
import { UpdateUserComponent } from './components/update-user/update-user.component';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import {
  NewTableData, TableSortEvent, TableBurgerClickEvent,
} from 'src/app/components/new-table/new-table.component';
import { StaffFiltersComponent } from '../staff-filters/staff-filters.component';
import { StaffFilter } from '../staff-filters/staffFilter';
import { EntityService } from 'src/app/services/entity.service';

@Component({
  selector: 'app-staff-list',
  templateUrl: './staff-list.component.html',
  styleUrls: ['./staff-list.component.scss'],
})
export class StaffListComponent implements OnInit, OnDestroy {
  loading = false;
  filterString = '';
  tableData: NewTableData;
  userListData: any;
  permissions: any;
  selectedItemsPerPage = 25;
  isActive = false;

  searchText = '';
  sortColumnName = 'firstName';
  sortDirection: 'ASC' | 'DESC' = 'ASC';
  offset = 0;

  // Filter properties
  mainFilter: StaffFilter = new StaffFilter();
  districtPrimaryEmail = '';

  // #region Subscriptions
  entitySubscription = this.entityService.entityData$.subscribe((entityData: any) => {
    if (entityData) {
      this.districtPrimaryEmail = entityData.primaryEmail || '';
    }
  });

  userListSubscription = this.usersService.usersData$.subscribe((data: any) => {
    if (!data) return;
    this.userListData = data;
    const formattedTableData = this?.userListData?.rows?.map((user: any) => {
      const isDistrictPrimary = user?.contactEmail === this.districtPrimaryEmail;
      return {
        itemId: user.id,
        rowColor: isDistrictPrimary ? 'district-primary' : 'default',
        columnData: [
          {
            cellType: 'avatarCell',
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.contactEmail || '',
            size: 'md',
          },
          {
            type: 'general',
            mainText: user?.PrimaryPosition?.positionTitle || 'No Position',
            mainFontStyle: 'Bold',
            subText: user?.PrimaryBuilding?.buildingName || 'No Building',
            subFontStyle: 'normal',
          },
          {
            type: 'status',
            statusDisplay: user?.activeFlag === 1 ? 'Active' : 'Inactive',
            statusKid: 'active',
            statusType: 'active',
          },
          {
            type: 'role',
            roleDisplay: user?.Roles?.[0]?.roleName || 'No Role',
            roleKid: isDistrictPrimary ? 'DISTRICT_PRIMARY' : (user?.Roles?.[0]?.kalpaIdentifier || 'default'),
            statusType: 'role',
          },
        ],
      }
    })

    this.tableData = {
      title: 'Staff',
      rowStyling: 'default',
      displayNoDataMessage: true,
      noDataFoundMessage: 'No staff members found.',
      columnTitles: [
        {
          name: 'User',
          sortable: true,
          sortByProp: 'firstName',
          sortDirection: this.sortDirection,
          width: '30%',
        },
        {
          name: 'Position/Location',
          sortable: true,
          sortByProp: 'PrimaryPosition.position_title',
          sortDirection: this.sortDirection,
          width: '30%',
        },
        {
          name: 'Status',
          sortable: false,
          sortByProp: 'activeFlag',
          sortDirection: this.sortDirection,
          width: '15%',
        },
        {
          name: 'User Role',
          sortable: false,
          sortByProp: 'Roles.role_name',
          width: '25%',
        },
      ],
      data: formattedTableData,
      hasBurgerMenu: this.permissions?.USER_EDIT,
      burgerContent: [
        {
          content: 'Edit Staff Member',
          eventName: 'edit',
          displayCondition: () => this.permissions?.USER_EDIT,
        },
      ],
      meta: {
        totalItems: data?.pagination.totalItems || 0,
        itemsPerPage: this.selectedItemsPerPage,
        curPage: data?.pagination.curPage || 1,
        totalPages: data?.pagination.totalPages || 1,
        usePagination: true,
        links: {
          prev: '',
          next: '',
          first: '',
          last: '',
          self: '',
        },
      },
    }
    this.userListData ? this.loading = false : null;
  })

  authSubscription = this.authService.permissions$.subscribe((permissions) => {
    this.permissions = permissions;

    // Check permissions and set showOnlyAssignedToMe filter
    const showOnlyAssignedToMe = !this.permissions?.USER_ADD &&
      !this.permissions?.USER_EDIT &&
      !this.permissions?.MANAGE_ALL_AUTHORITY &&
      !this.permissions?.USER_ACCOUNTS;

    this.mainFilter.showOnlyAssignedToMe = showOnlyAssignedToMe;

    // Trigger search with updated filter if table is initialized
    if (this.tableData) {
      this.searchForUsers();
    }
  });
  // #endregion

  constructor(
    private usersService: UsersService,
    public dialog: MatDialog,
    private authService: AuthService,
    private router: Router,
    private entityService: EntityService,
  ) { }

  ngOnInit(): void {
    // Load entity data to get current user's email
    this.entityService.getEntity('');

    this.tableData = {
      title: 'Staff',
      rowStyling: 'default',
      displayNoDataMessage: true,
      noDataFoundMessage: 'No staff members found.',
      columnTitles: [
        {
          name: 'User',
          sortable: true,
          sortByProp: 'firstName',
          isDefaultSort: true,
          sortDirection: 'ASC',
          width: '30%',
        },
        {
          name: 'Position/Location',
          sortable: true,
          sortByProp: 'PrimaryPosition.position_title',
          width: '30%',
        },
        {
          name: 'Status',
          sortable: false,
          sortByProp: 'activeFlag',
          width: '15%',
        },
        {
          name: 'User Role',
          sortable: false,
          sortByProp: 'Roles.role_name',
          width: '25%',
        },
      ],
      data: [],
      hasBurgerMenu: true,
      burgerContent: [
        {
          content: 'Edit Staff Member',
          eventName: 'edit',
        },
      ],
      meta: {
        totalItems: 0,
        itemsPerPage: this.selectedItemsPerPage,
        curPage: 1,
        totalPages: 1,
        usePagination: true,
        links: {
          prev: '',
          next: '',
          first: '',
          last: '',
          self: '',
        },
      },
    };

    this.loading = true;
    this.searchForUsers();
  }

  searchForUsers() {
    this.loading = true;
    this.tableData.data = [];
    const includedAssociations = 'PrimaryBuilding, PrimaryPosition, Roles'

    // Build roleAccessLevels array based on checkbox states (respect user selections)
    const roleAccessLevels: string[] = [];

    // Add role filters based on user selections, regardless of permissions
    if (this.mainFilter.showBasicMembers) {
      roleAccessLevels.push('BASIC');
    }

    if (this.mainFilter.showDefaultAdministrators) {
      roleAccessLevels.push('MANAGE_ALL_AUTHORITY');
    }

    if (this.mainFilter.showDefaultSupervisors) {
      roleAccessLevels.push('MANAGE_SECONDARY_AUTHORITY');
    }

    if (this.mainFilter.showCustomRoles) {
      roleAccessLevels.push('CUSTOM');
    }

    const params = {
      limit: this.selectedItemsPerPage,
      offset: this.offset,
      sortColumn: this.sortColumnName,
      sortDirection: this.sortDirection,
      activeFlag: this.mainFilter.showInactiveMembers ? 0 : 1 as 0 | 1,
      userNameFilter: this.searchText,
      includedAssociations,
      // Filter parameters mapped from checkboxes
      showAssignedToMe: this.mainFilter.showOnlyAssignedToMe ? 1 : 0,
      roleAccessLevels: roleAccessLevels.length > 0 ? roleAccessLevels.join(',') : undefined,
    }

    // Remove undefined parameters to avoid sending them to the API
    Object.keys(params).forEach(key => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });

    this.usersService.getUsers(params);
  }

  calculateOffset(page: number): number {
    return (page - 1) * this.selectedItemsPerPage;
  }

  onPageChanged(page: number) {
    const offset = this.calculateOffset(page);
    this.offset = offset;
    this.searchForUsers();
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.selectedItemsPerPage = itemsPerPage;
    this.offset = 0;
    this.searchForUsers();
  }

  onSortClicked(sortEvent: TableSortEvent) {
    const { columnName, sortDirection } = sortEvent;
    this.sortColumnName = columnName;
    this.sortDirection = sortDirection;
    this.offset = 0;

    // Update the table column sort directions to reflect the current sort state
    this.tableData.columnTitles.forEach(col => {
      if (col.sortByProp === columnName) {
        col.sortDirection = sortDirection;
      } else {
        col.sortDirection = null;
      }
    });

    this.searchForUsers();
  }

  onBurgerMenuClicked(event: TableBurgerClickEvent) {
    if (event.eventName === 'edit') {
      this.showStaffEditModal(event.itemId);
    }
  }

  onPaginationEvent(pageDirection: string) {
    this.loading = true;
    this.tableData.data = [];
    this.usersService.getPagination(pageDirection);
  }

  addNewStaff = () => {
    const dialog = this.dialog.open(UpdateUserComponent);

    dialog.afterClosed().subscribe((result) => {
      if (result.statusCode === 1000 || result === 'reload') {
        this.searchForUsers();
      }
    });
  }

  showStaffEditModal = (itemId: any) => {
    const user = this.userListData.rows.find((user: any) => user.id === itemId);

    const editModal = this.dialog.open(UpdateUserComponent, { data: user })

    editModal.afterClosed().subscribe((result) => {
      if (result?.statusCode === 1000 || result === 'reload') {
        this.searchForUsers();
      }
    });
  }

  onRowClicked(itemId: number) {
    const itemRouterLink = `/profile/${itemId}`;
    this.router.navigate([itemRouterLink]);

  }

  onSearchInput(searchValue: string) {
    this.searchText = searchValue;
    this.searchForUsers();
  }

  openFiltersModal = () => {
    const dialogRef = this.dialog.open(StaffFiltersComponent, {
      data: this.mainFilter,
      width: '500px',
      disableClose: true,
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result && !result.noChange) {
        this.loading = true;
        this.mainFilter = result.filter;
        this.offset = 0;
        this.searchForUsers();
      } else if (result && result.noChange) {
        this.loading = false;
      }
    })
  }

  ngOnDestroy(): void {
    this.userListSubscription.unsubscribe();
    this.authSubscription.unsubscribe();
    this.entitySubscription.unsubscribe();
  }

}
